// React Hooks
import { useEffect, useRef, useState } from "react";

// Redux
import { useDispatch, useSelector } from "react-redux";
import { setDish1, setDish2, setDish3, setResponse } from "../../redux/dishes";
import { decreamentDishTokens } from "../../redux/tokens";

// Third-party libraries
import { PropagateLoader, SyncLoader } from "react-spinners";
import {
  IoArrowBackCircleSharp,
  IoArrowForwardCircleSharp,
  IoSearch,
} from "react-icons/io5";

// Firebase
import { collection, getDocs } from "firebase/firestore";
import { firestoredDB } from "../../config/firebase";

// Utils
import { getRandomJoke } from "../../utils/jokes";
import TextToSpeech from "../TextToSpeech";
import { CButton } from "../CommonComponents";
import {
  generateDishesCompletion,
  generateImageService,
} from "../../utils/apiServices/openAIServices";
import { Dark_White_mode } from "../../utils/constants";
import { updateUserTokensV2 } from "../../utils/apiServices/usersService";
import { useDarkMode } from "../../DarkModeProvider";
import { useNavigate } from "react-router-dom";

export default function GenerateDishes({
  prompt,
  history,
  displayedText,
  setDisplayedText,
  onDishClick,
  getVariant,
  handleBackClick,
  recipeTitle,
}) {
  const dispatch = useDispatch();
  const {
    user: { uid },
  } = useSelector((state) => state.auth.user);
  const { dish1, response } = useSelector((state) => state.dish);

  const topRef = useRef(null);
  const navigate = useNavigate();
  const { isDarkMode } = useDarkMode();
  const [result, setResult] = useState();
  const [loading, setLoading] = useState(false);
  const [oldResponse, setOldResponse] = useState("");
  const [intervalId, setIntervalId] = useState(null);
  const [showTextToSpeech, setShowTextToSpeech] = useState(false);
  const [dishes, setDishes] = useState({
    about_dishes: "",
    about_culture: "",
    dishes: [],
  });
  const [imageLoading, setImageLoading] = useState(false);
  const [joke, setJoke] = useState("");
  const { dishTokens, recipeTokens } = useSelector((state) => state.tokens);

  const jokesRef = collection(firestoredDB, "Jokes");

  const getImagesForDishes = async (prevData, prompts) => {
    setImageLoading(true);

    try {
      const imageUrls = await Promise.all(
        prompts.map((prompt) => generateImageService(prompt, "512"))
      );

      const dishesLocal = prevData?.dishes;
      const updatedData = dishesLocal?.map((item, index) => ({
        ...item,
        image: imageUrls[index], // Update the image field
      }));

      const finalDataWithImages = {
        ...prevData,
        dishes: updatedData,
      };
      setDishes(finalDataWithImages);
      dispatch(setResponse(finalDataWithImages));
      setResult(finalDataWithImages);
      setImageLoading(false);
    } catch (e) {
      console.log(e);
      setImageLoading(false);
    }

    setImageLoading(false);
  };

  const generateRecipe = async () => {
    setLoading(true);

    const data = await generateDishesCompletion(prompt, history);

    const titles = [...data.matchAll(/"title":\s*"(.*?)"/g)].map(
      (match) => match[1]
    );

    const summaries = [...data.matchAll(/"summary":\s"(.*?)"/g)].map(
      (match) => match[1]
    );
    const images = [...data.matchAll(/"image_prompt":\s"(.*?)"/g)].map(
      (match) => match[1]
    );

    const about = [...data.matchAll(/"about_dishes":\s"(.*?)"/g)].map(
      (match) => match[1]
    );
    const about_culture = [...data.matchAll(/"about_culture":\s"(.*?)"/g)].map(
      (match) => match[1]
    );
    const extractedData = titles.map((title, index) => ({
      title,
      summary: summaries[index] ?? "",
      image: "",
    }));

    setLoading(false);
    const finalData = {
      ...dishes,
      about_dishes: about[0],
      about_culture: about_culture[0],
      dishes: extractedData,
    };
    setDishes(finalData);
    dispatch(setResponse(finalData));
    setResult(finalData);

    const dbRes = await updateUserTokensV2(uid, "dish");

    if (dbRes) {
      dispatch(decreamentDishTokens());
    }

    await getImagesForDishes(finalData, images);
  };

  useEffect(() => {
    if (dish1?.length == 0) {
      setJoke("");
      getJoke();
      generateRecipe();
    } else {
      if (response) {
        setDishes(response);
      }
    }
  }, []);

  useEffect(() => {
    if (history?.length != 0) {
      setJoke("");
      getJoke();
      generateRecipe();
    }
  }, [history]);

  // joke

  const getJoke = () => {
    const randomIndex = Math.floor(Math.random() * 3);

    switch (randomIndex) {
      case 0:
        getRandomJoke(jokesRef, getDocs, setJoke, "alpha");
        break;
      case 1:
        getRandomJoke(jokesRef, getDocs, setJoke, "beta");
        break;
      case 2:
        getRandomJoke(jokesRef, getDocs, setJoke, "gamma");
        break;
    }
  };

  useEffect(() => {
    // Function to start the joke interval
    const startJokeInterval = () => {
      if (!intervalId) {
        const id = setInterval(() => {
          getJoke();
        }, 15000); // 15 seconds interval
        setIntervalId(id);
      }
    };

    // Function to clear the joke interval
    const clearJokeInterval = () => {
      if (intervalId) {
        clearInterval(intervalId);
        setIntervalId(null);
      }
    };

    if (loading) {
      startJokeInterval();
    } else {
      clearJokeInterval();
    }

    // Cleanup function to clear interval when component unmounts
    return () => {
      clearJokeInterval();
    };
  }, [loading]); // Dependency array includes 'loading' to re-run effect when it changes

  useEffect(() => {
    if (result) {
      dispatch(setDish1(result?.dishes[0]?.title));
      dispatch(setDish2(result?.dishes[1]?.title));
      dispatch(setDish3(result?.dishes[2]?.title));
    }
  }, [result]);

  useEffect(() => {
    if (topRef.current) {
      topRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [displayedText, loading]);

  return (
    <div
      className="flex w-full justify-center items-center flex-col flex-wrap"
      ref={topRef}
    >
      {/* <div ref={topRef} className="ref-class"></div> */}
      {loading ? (
        <div className="flex flex-col justify-center items-center md:w-[80%] mt-[10%]">
          <IoSearch className="text-4xl text-[#fc0]" />

          <h1
            className={`${Dark_White_mode} my-6 h-full w-[60%] mx-auto text-center md:text-xl text-md`}
          >
            {joke.trim()}
          </h1>

          <div className="my-6 w-full mx-auto flex items-center justify-center">
            <PropagateLoader color="#FFAA12" />
          </div>
        </div>
      ) : (
        <>
          <div className="">
            <div className="flex items-start justify-between  mb-4 gap-4">
              <div>
                <div onClick={handleBackClick} className="my-3 cursor-pointer">
                  <IoArrowBackCircleSharp
                    size={40}
                    color={isDarkMode ? "white" : "black"}
                  />
                </div>
              </div>
              <div className="max-w-[80%] lg:w-full">
                <p
                  className={`${Dark_White_mode} text-[30px] font-semibold mb-2 text-center`}
                >
                  Let's cook up something delicious!
                </p>
                <p className={`${Dark_White_mode} text-[22px] text-center`}>
                  {dishes?.about_dishes}
                </p>
              </div>
              <div>
                {recipeTitle && (
                  <div
                    onClick={() => {
                      navigate(-1);
                    }}
                    className="my-3 cursor-pointer"
                  >
                    <IoArrowForwardCircleSharp
                      size={40}
                      color={isDarkMode ? "white" : "black"}
                    />
                  </div>
                )}
              </div>
            </div>

            <p className={`${Dark_White_mode} mb-6 text-center`}>
              {dishes?.about_culture}
            </p>
            <div className="grid lg:grid-cols-3 sm:grid-cols-2 grid-cols-1 gap-4">
              {dishes?.dishes?.map((item, index) => (
                <div className="flex flex-col justify-between dark:bg-orange-300 p-4 w-full rounded-lg text-black bg-white mb-6">
                  <div className="flex flex-col items-start justify-center gap-4 mb-4">
                    <div className="w-full h-48 shrink-0">
                      {imageLoading ? (
                        <p>Image is Loading...</p>
                      ) : (
                        <img
                          src={item?.image}
                          alt={item?.title}
                          className="rounded-lg w-full h-full object-cover"
                        />
                      )}
                    </div>
                    <div>
                      {" "}
                      <div className="flex items-center justify-between">
                        <p className="font-semibold mb-1">{item?.title}</p>
                        {/* <p className="font-semibold mb-1 font-lg cursor-pointer">
                          +
                        </p> */}
                      </div>
                      <p className="text-gray-600 ">{item?.summary}</p>
                    </div>{" "}
                  </div>

                  <CButton
                    variant={"red"}
                    label={"Get Recipe"}
                    className={"w-full"}
                    onClick={() => {
                      onDishClick({
                        dishNumber: index + 1,
                        dishName: item?.title,
                        imageUrl: item?.image,
                      });
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
          <CButton
            variant={"orange"}
            label={"+ Load 3 New Dishes"}
            className={"w-full"}
            onClick={getVariant}
          />
          <div className="w-full">
            {/* <pre className="mb-7 text-gray-200 md:text-lg text-sm">
              <ReactMarkdown>{oldResponse || displayedText}</ReactMarkdown>
            </pre> */}
          </div>
          {showTextToSpeech && <TextToSpeech displayedText={displayedText} />}
        </>
      )}
    </div>
  );
}
